import { Provider } from "@project-serum/anchor";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { getSolletWallet } from "@solana/wallet-adapter-wallets";
import {
  ConfirmOptions,
  Connection, PublicKey, Signer, Transaction, TransactionConfirmationStatus, TransactionSignature
} from "@solana/web3.js";
import { poll } from "poll";

// Cast wallet to AnchorWallet in order to be compatible with Anchor's Provider class
interface AnchorWallet {
  signTransaction(tx: Transaction): Promise<Transaction>;
  signAllTransactions(txs: Transaction[]): Promise<Transaction[]>;
  publicKey: PublicKey;
}

// Custom provider to display notifications whenever a transaction is sent.
//
// Note that this is an Anchor wallet/network provider--not a React provider,
// so all transactions will be flowing through here, which allows us to
// hook in to display all transactions sent from the `Swap` component
// as notifications in the parent app.
export default class NotifyingProvider extends Provider {
  // Function to call whenever the provider sends a transaction;
  private onTransaction: (
    tx: TransactionSignature | undefined,
    confirmed?: boolean,
    err?: Error
  ) => void;

  constructor(
    connection: Connection,
    wallet: any,
    opts: ConfirmOptions,
    // Callback to trigger notification
    onTransaction: (
      tx: TransactionSignature | undefined,
      confirmed?: boolean,
      err?: Error
    ) => void
  ) {
    if (!!wallet) super(connection, wallet as AnchorWallet, opts);
    else // to silent down errors for swap-ui expections
      super(
        connection,
        getSolletWallet({
          network: WalletAdapterNetwork.Mainnet,
        }).adapter() as AnchorWallet,
        opts
      );
    this.onTransaction = onTransaction;
  }

  async send(
    tx: Transaction,
    signers?: Array<Signer | undefined>,
    opts?: ConfirmOptions
  ) {
    try {
      if (signers === undefined) {
        signers = [];
      }
      if (opts === undefined) {
        opts = this.opts;
      }
      tx.feePayer = this.wallet.publicKey;

      tx.recentBlockhash = (
        await this.connection.getRecentBlockhash(opts.preflightCommitment)
      ).blockhash;

      await this.wallet.signTransaction(tx);
      signers
        .filter((s) => s !== undefined)
        .forEach((kp) => {
          tx.partialSign(kp!);
        });

      const rawTx = tx.serialize();

      const txId = await this.connection.sendRawTransaction(rawTx, opts);

      let confirmed: TransactionConfirmationStatus | undefined;
      this.onTransaction(txId);

      const interval = 2000;
      let retries = 15;

      await poll(
        async () => {
          --retries;

          const sigStatus = await this.connection.getSignatureStatus(txId);
          console.log("Signature status", sigStatus);
          confirmed = sigStatus.value?.confirmationStatus;

          if (sigStatus.value?.err) {
            throw Error("Transaction " + txId + " failed");
          } else if (confirmed) {
            this.onTransaction(txId, true);
          }
        },
        interval,
        () => {
          // Poll until confirmed or out of retries
          return confirmed !== undefined || retries === 0;
        }
      );

      if (confirmed === undefined && retries === 0) {
        throw Error("Failed to confirm transaction " + txId);
      }

      console.log("Result returned");
      return txId;
    } catch (err) {
      if (err instanceof Error || err === undefined) {
        this.onTransaction(undefined, undefined, err);
      }
      return "";
    }
  }

  async sendAll(
    reqs: Array<{ tx: Transaction; signers: Array<Signer | undefined> }>,
    opts?: ConfirmOptions
  ): Promise<Array<TransactionSignature>> {
    try {
      if (opts === undefined) {
        opts = this.opts;
      }
      const blockhash = await this.connection.getRecentBlockhash(
        opts.preflightCommitment
      );

      // Prepare transactions
      let txs = reqs.map((r) => {
        let tx = r.tx;
        let signers = r.signers;

        if (signers === undefined) {
          signers = [];
        }

        tx.feePayer = this.wallet.publicKey;
        tx.recentBlockhash = blockhash.blockhash;

        signers
          .filter((s) => s !== undefined)
          .forEach((kp) => {
            tx.partialSign(kp!);
          });

        return tx;
      });
      const signedTxs = await this.wallet.signAllTransactions(txs);

      const sentTxSigs: TransactionSignature[] = [];

      // Send transactions one after another
      for (const tx of signedTxs) {
        const rawTx = tx.serialize();
        const txId = await this.connection.sendRawTransaction(rawTx, opts);
        this.onTransaction(txId);
        sentTxSigs.push(txId);
      }

      const unverifiedSigs = [...sentTxSigs];
      const interval = 2000;
      let retries = 15;

      // Poll until all signatures are verified
      await poll(
        async () => {
          --retries;

          const sigStatuses = await this.connection.getSignatureStatuses(
            unverifiedSigs
          );
          console.log("Signature statuses", sigStatuses);

          const confirmedIndexes: number[] = [];
          // If a signature is confirmed, display notification and pop it from sigs array
          for (let index = 0; index < sigStatuses.value.length; index++) {
            const status = sigStatuses.value[index];
            if (status?.err) {
              throw Error("Transaction " + unverifiedSigs[index] + " failed");
            } else if (status?.confirmationStatus) {
              // Display confirmed notification
              this.onTransaction(unverifiedSigs[index], true);

              // Mark as confirmed
              confirmedIndexes.push(index);
            }
          }
          // Remove verified signatures from list
          confirmedIndexes.forEach((confirmedIndex) =>
            unverifiedSigs.splice(confirmedIndex, 1)
          );
        },
        interval,
        () => {
          // Poll until all confirmed or out of retries
          return unverifiedSigs.length === 0 || retries === 0;
        }
      );

      if (unverifiedSigs.length > 0) {
        throw Error("Failed to confirm transaction " + unverifiedSigs[0]);
      }

      return sentTxSigs;
    } catch (err) {
      if (err instanceof Error || err === undefined) {
        this.onTransaction(undefined, undefined, err);
      }
      return [];
    }
  }
}
